import { useState, useCallback } from 'react';

/**
 * Custom hook for managing localStorage.
 *
 * @param {string} key - localStorage key
 * @param {TValue} initialValue - initial value to use if no stored value exists
 * @returns {Array}
 */
export const useLocalStorage = <TValue>(key: string, initialValue: TValue) => {
    // Get value from localStorage or use initial value
    const [storedValue, setStoredValue] = useState<TValue>(() => {
        try {
            const item = window.localStorage.getItem(key);

            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Error reading localStorage key "${key}":`, error);

            return initialValue;
        }
    });

    // Save to localStorage when value changes
    const setValue = useCallback(
        (value: TValue | ((val: TValue) => TValue)) => {
            try {
                setStoredValue(currentValue => {
                    const valueToStore = value instanceof Function ? value(currentValue) : value;
                    window.localStorage.setItem(key, JSON.stringify(valueToStore));

                    return valueToStore;
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(`Error setting localStorage key "${key}":`, error);
            }
        },
        [key]
    );

    return [storedValue, setValue] as const;
};
